import { useEffect, useRef, useState } from 'react'
import { Check, CheckSquare, ChevronLeft, ChevronRight, Download, Loader2, MessageSquare, Plus, Send, Share2, Square, Trash2, X, ArrowLeft } from 'lucide-react'
import { useImages } from '@/hooks/useApi'
import { getToken } from '@/lib/auth'
import { api } from '@/lib/api'
import { useQueryClient } from '@tanstack/react-query'

const API_BASE = '/api/dashboard/images'
const LONG_PRESS_MS = 500

function fixServeUrl(url: string | undefined, imageId: string): string {
  if (!url) return `${API_BASE}/${imageId}`
  return url.replace(/https?:\/\/ops-bridge[^/]*\.workers\.dev/, '/api')
}

interface ImageMeta {
  image_id: string
  prompt: string
  size: string
  quality: string
  created_at: string
  serve_url: string
  model_used?: string
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface VisionAttachment {
  name: string
  data: string
  mimeType: string
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const value = String(reader.result || '')
      resolve(value.includes(',') ? value.slice(value.indexOf(',') + 1) : value)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function fileToAttachment(file: File): Promise<VisionAttachment> {
  return { name: file.name, data: await blobToBase64(file), mimeType: file.type || 'image/jpeg' }
}

async function libraryImageToAttachment(image: ImageMeta): Promise<VisionAttachment> {
  const res = await fetch(fixServeUrl(image.serve_url, image.image_id), {
    headers: { Authorization: `Bearer ${getToken()}` },
  })
  if (!res.ok) throw new Error(`Could not load image pixels (${res.status})`)
  const blob = await res.blob()
  return {
    name: `${image.image_id}.${blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg'}`,
    data: await blobToBase64(blob),
    mimeType: blob.type || 'image/jpeg',
  }
}

function GridCell({ image, selectMode, selected, onOpen, onToggle, onLongPress }: {
  image: ImageMeta
  selectMode: boolean
  selected: boolean
  onOpen: () => void
  onToggle: () => void
  onLongPress: () => void
}) {
  const timer = useRef<number | null>(null)
  const longPressed = useRef(false)

  const start = () => {
    longPressed.current = false
    timer.current = window.setTimeout(() => {
      longPressed.current = true
      onLongPress()
    }, LONG_PRESS_MS)
  }
  const cancel = () => {
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = null
  }
  const end = () => {
    cancel()
    if (!longPressed.current) selectMode ? onToggle() : onOpen()
  }

  return (
    <button
      type="button"
      className="group relative aspect-square overflow-hidden rounded-xl bg-surface-elevated text-left focus:outline-none focus:ring-2 focus:ring-accent/50"
      onPointerDown={start}
      onPointerUp={end}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={e => e.preventDefault()}
      aria-label={selectMode ? `Select image ${image.image_id}` : `Open image ${image.image_id}`}
    >
      <img
        src={fixServeUrl(image.serve_url, image.image_id)}
        alt={image.prompt || 'Generated image'}
        loading="lazy"
        draggable={false}
        className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.02] group-hover:brightness-95"
      />
      {selectMode && (
        <span className={`absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border shadow-lg ${selected ? 'border-accent bg-accent text-white' : 'border-white/50 bg-black/45 text-white'}`}>
          {selected ? <Check size={15} /> : <Square size={14} />}
        </span>
      )}
    </button>
  )
}

function ChatBubble({ message }: { message: ChatMessage }) {
  const user = message.role === 'user'
  return (
    <div className={`flex ${user ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${user ? 'bg-accent text-white rounded-br-md' : 'bg-surface-elevated text-text-primary rounded-bl-md'}`}>
        {message.content}
      </div>
    </div>
  )
}

function ImageViewer({ images, initialIndex, onClose }: { images: ImageMeta[]; initialIndex: number; onClose: () => void }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const image = images[currentIndex]
  const [discussing, setDiscussing] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [thinking, setThinking] = useState(false)
  const [attachedFile, setAttachedFile] = useState<File | null>(null)
  const [visionError, setVisionError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const chatEnd = useRef<HTMLDivElement>(null)
  const swipeStartX = useRef<number | null>(null)
  const swipeStartTime = useRef(0)
  const activePointerId = useRef<number | null>(null)
  const [dragX, setDragX] = useState(0)
  const [settling, setSettling] = useState(false)
  const [settleDirection, setSettleDirection] = useState<-1 | 0 | 1>(0)
  const imageUrl = fixServeUrl(image.serve_url, image.image_id)
  const previousIndex = (currentIndex - 1 + images.length) % images.length
  const nextIndex = (currentIndex + 1) % images.length
  const previousImage = images[previousIndex]
  const nextImage = images[nextIndex]

  const resetImageConversation = () => {
    setMessages([])
    setInput('')
    setAttachedFile(null)
    setVisionError(null)
  }

  const changeImage = (direction: -1 | 1) => {
    if (images.length < 2 || discussing || settling) return
    setCurrentIndex(index => (index + direction + images.length) % images.length)
    resetImageConversation()
  }

  const finishSwipe = (direction: -1 | 1) => {
    if (settling || images.length < 2) return
    setSettleDirection(direction)
    setSettling(true)
  }

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages, thinking])
  useEffect(() => {
    if (images.length < 2) return
    ;[previousImage, nextImage].forEach(adjacent => {
      const preload = new Image()
      preload.src = fixServeUrl(adjacent.serve_url, adjacent.image_id)
    })
  }, [currentIndex, images.length])
  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') changeImage(-1)
      if (e.key === 'ArrowRight') changeImage(1)
    }
    window.addEventListener('keydown', key)
    return () => {
      window.removeEventListener('keydown', key)
      document.body.style.overflow = previousOverflow
    }
  }, [onClose, discussing, images.length])

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'OpsBridge Image', text: image.prompt.slice(0, 120), url: imageUrl }); return } catch { /* cancelled */ }
    }
    await navigator.clipboard.writeText(imageUrl).catch(() => {})
  }

  const downloadImage = async () => {
    try {
      const res = await fetch(imageUrl, { headers: { Authorization: `Bearer ${getToken()}` } })
      if (!res.ok) throw new Error(`Download failed (${res.status})`)
      const blob = await res.blob()
      const extension = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : blob.type.includes('gif') ? 'gif' : 'jpg'
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = `${image.image_id}.${extension}`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    } catch {
      window.alert('Download failed. Please try again.')
    }
  }

  const beginDiscussion = () => {
    setDiscussing(true)
    if (messages.length === 0) {
      setMessages([{ role: 'assistant', content: 'What would you like to change, explore, or create from this image?' }])
    }
  }

  const send = async () => {
    if ((!input.trim() && !attachedFile) || thinking) return
    const text = input.trim() || 'Compare the attached image with this generated image.'
    const next: ChatMessage[] = [...messages, { role: 'user', content: attachedFile ? `${text}\n\nAttached comparison image: ${attachedFile.name}` : text }]
    setMessages(next)
    setInput('')
    setThinking(true)
    setVisionError(null)

    try {
      const attachments: VisionAttachment[] = [await libraryImageToAttachment(image)]
      if (attachedFile) attachments.push(await fileToAttachment(attachedFile))
      setAttachedFile(null)
      if (fileInput.current) fileInput.current.value = ''

      const context = [
        'The user is discussing an AI-generated image from the OpsBridge image library. The actual image pixels are attached to this request for visual analysis.',
        `Image ID: ${image.image_id}`,
        `Original generation prompt: ${image.prompt}`,
        `Original size: ${image.size}`,
        'Discuss the visible image itself, not merely the prompt. If the user requests a change, explain or carry out the appropriate image-generation workflow subject to normal platform permissions.',
      ].join('\n')

      const history = [
        { role: 'user', content: `${context}\n\nUser request: ${text}` },
        ...next.slice(1).map(m => ({ role: m.role, content: m.content })),
      ]

      const res = await api.chatStream(history, 'enterprise', undefined, undefined, attachments)
      if (!res.ok || !res.body) throw new Error(`AI endpoint returned ${res.status}`)

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let accumulated = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const raw = line.slice(6).trim()
          if (!raw || raw === '[DONE]') continue
          try {
            const event = JSON.parse(raw)
            if (event.type === 'content_block_delta' && event.delta?.text) accumulated += event.delta.text
            else if (event.type === 'text_delta' && event.text) accumulated += event.text
            else if (typeof event.content === 'string') accumulated += event.content
          } catch { /* ignore malformed SSE event */ }
        }
      }
      setMessages(current => [...current, { role: 'assistant', content: accumulated || 'No response received.' }])
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not reach OpsBridge AI.'
      setVisionError(message)
      setMessages(current => [...current, { role: 'assistant', content: `I couldn't analyse the image right now. ${message}` }])
    } finally {
      setThinking(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm" onMouseDown={e => { if (e.target === e.currentTarget && !discussing) onClose() }}>
      <div className="flex h-full w-full flex-col">
        <div className="flex h-14 shrink-0 items-center justify-between px-4 sm:px-6">
          <button onClick={() => discussing ? setDiscussing(false) : onClose()} className="flex h-9 items-center gap-2 rounded-full px-3 text-sm text-white/80 hover:bg-white/10 hover:text-white">
            {discussing ? <><ArrowLeft size={17} /> Image</> : <><X size={18} /> Close</>}
          </button>
          <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full text-white/80 hover:bg-white/10 hover:text-white" aria-label="Close viewer"><X size={19} /></button>
        </div>

        {!discussing ? (
          <div
            className="relative min-h-0 flex-1 overflow-hidden select-none"
            style={{ touchAction: 'pan-y' }}
            onPointerDown={e => {
              if (settling || images.length < 2) return
              swipeStartX.current = e.clientX
              swipeStartTime.current = performance.now()
              activePointerId.current = e.pointerId
              e.currentTarget.setPointerCapture(e.pointerId)
              setDragX(0)
            }}
            onPointerMove={e => {
              if (swipeStartX.current === null || activePointerId.current !== e.pointerId || settling) return
              setDragX(e.clientX - swipeStartX.current)
            }}
            onPointerUp={e => {
              if (swipeStartX.current === null || activePointerId.current !== e.pointerId) return
              const delta = e.clientX - swipeStartX.current
              const elapsed = Math.max(performance.now() - swipeStartTime.current, 1)
              const velocity = delta / elapsed
              swipeStartX.current = null
              activePointerId.current = null
              if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
              const threshold = Math.min(window.innerWidth * 0.2, 140)
              if (Math.abs(delta) >= threshold || Math.abs(velocity) > 0.55) finishSwipe(delta < 0 ? 1 : -1)
              else {
                setSettleDirection(0)
                setSettling(true)
              }
            }}
            onPointerCancel={e => {
              swipeStartX.current = null
              activePointerId.current = null
              if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
              setSettleDirection(0)
              setSettling(true)
            }}
          >
            <div
              className="absolute inset-y-0 left-0 flex"
              style={{
                width: '300vw',
                transform: `translate3d(calc(-100vw + ${settling ? (settleDirection === 1 ? '-100vw' : settleDirection === -1 ? '100vw' : '0px') : `${dragX}px`}), 0, 0)`,
                transition: settling ? 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
                willChange: 'transform',
              }}
              onTransitionEnd={() => {
                if (!settling) return
                if (settleDirection !== 0) {
                  setCurrentIndex(index => (index + settleDirection + images.length) % images.length)
                  resetImageConversation()
                }
                setDragX(0)
                setSettleDirection(0)
                setSettling(false)
              }}
            >
              {[previousImage, image, nextImage].map((slide, slideIndex) => (
                <div key={`${slide.image_id}-${slideIndex}`} className="flex h-full w-screen shrink-0 items-center justify-center overflow-hidden">
                  <img src={fixServeUrl(slide.serve_url, slide.image_id)} alt={slide.prompt} draggable={false} className="block h-full w-full object-contain pointer-events-none" />
                </div>
              ))}
            </div>
            {images.length > 1 && (
              <>
                <button onClick={e => { e.stopPropagation(); changeImage(-1) }} className="absolute left-4 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white/80 backdrop-blur hover:bg-black/65 hover:text-white md:flex" aria-label="Previous image"><ChevronLeft size={25} /></button>
                <button onClick={e => { e.stopPropagation(); changeImage(1) }} className="absolute right-4 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white/80 backdrop-blur hover:bg-black/65 hover:text-white md:flex" aria-label="Next image"><ChevronRight size={25} /></button>
              </>
            )}
            <div className="absolute bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 flex -translate-x-1/2 items-center gap-3" onPointerDown={e => e.stopPropagation()} onPointerUp={e => e.stopPropagation()}>
              <button onClick={beginDiscussion} className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-black/55 text-white/80 shadow-xl backdrop-blur-xl hover:bg-white/10 hover:text-white" aria-label="Discuss image" title="Discuss image"><MessageSquare size={18} /></button>
              <button onClick={() => void downloadImage()} className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-black/55 text-white/80 shadow-xl backdrop-blur-xl hover:bg-white/10 hover:text-white" aria-label="Download image" title="Download image"><Download size={18} /></button>
              <button onClick={share} className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-black/55 text-white/80 shadow-xl backdrop-blur-xl hover:bg-white/10 hover:text-white" aria-label="Share image" title="Share image"><Share2 size={18} /></button>
            </div>
          </div>
        ) : (
          <div className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col gap-4 px-3 pb-3 md:flex-row md:px-5 md:pb-5">
            <div className="flex h-[30vh] shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-black/35 md:h-auto md:w-[48%]">
              <img src={imageUrl} alt={image.prompt} className="h-full w-full object-contain" />
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-white/10 bg-surface shadow-2xl">
              <div className="border-b border-border px-4 py-3">
                <div className="text-sm font-semibold text-text-primary">Discuss this image</div>
                <div className="mt-0.5 truncate text-xs text-text-muted">{image.prompt}</div>
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto p-4">
                {messages.map((message, index) => <ChatBubble key={index} message={message} />)}
                {thinking && <div className="flex items-center gap-2 text-xs text-text-muted"><Loader2 size={14} className="animate-spin" /> OpsBridge is looking at the image…</div>}
                {visionError && <div className="text-xs text-danger">{visionError}</div>}
                <div ref={chatEnd} />
              </div>
              {attachedFile && (
                <div className="mx-3 mb-2 flex items-center justify-between rounded-xl border border-accent/20 bg-accent/5 px-3 py-2 text-xs text-text-secondary">
                  <span className="truncate">{attachedFile.name}</span>
                  <button onClick={() => setAttachedFile(null)} className="ml-2 text-text-muted hover:text-text-primary"><X size={14} /></button>
                </div>
              )}
              <div className="border-t border-border p-3">
                <div className="flex items-end gap-2 rounded-2xl border border-border bg-surface-elevated p-2 focus-within:border-accent/50">
                  <button onClick={() => fileInput.current?.click()} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-text-muted hover:bg-surface hover:text-text-primary" title="Attach comparison image"><Plus size={17} /></button>
                  <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={e => setAttachedFile(e.target.files?.[0] || null)} />
                  <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send() } }} rows={1} placeholder="Ask about this image or describe a change…" className="max-h-28 min-h-9 flex-1 resize-none bg-transparent px-1 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none" />
                  <button onClick={() => void send()} disabled={(!input.trim() && !attachedFile) || thinking} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-white disabled:opacity-35"><Send size={16} /></button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function ImagesPanel() {
  const { data, isLoading } = useImages()
  const images: ImageMeta[] = data?.images ?? []
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [deleting, setDeleting] = useState(false)
  const queryClient = useQueryClient()

  const toggle = (id: string) => setSelectedIds(previous => {
    const next = new Set(previous)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const enterSelect = (id: string) => {
    setSelectMode(true)
    setSelectedIds(new Set([id]))
  }

  const exitSelect = () => {
    setSelectMode(false)
    setSelectedIds(new Set())
  }

  const removeSelected = async () => {
    if (!selectedIds.size) return
    if (!window.confirm(`Delete ${selectedIds.size} image${selectedIds.size === 1 ? '' : 's'}? This can't be undone.`)) return
    setDeleting(true)
    try {
      await api.imagesDelete(Array.from(selectedIds))
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'images'] })
      exitSelect()
    } catch {
      window.alert('Delete failed. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="min-h-full w-full">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0 text-xl font-semibold text-text-primary">Images</h2>
          <p className="mt-1 text-xs text-text-muted">{isLoading ? 'Loading…' : `${images.length} generated image${images.length === 1 ? '' : 's'}`}</p>
        </div>
        {selectMode && (
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds(selectedIds.size === images.length ? new Set() : new Set(images.map(image => image.image_id)))} className="flex h-9 items-center gap-2 rounded-lg border border-border px-3 text-xs text-text-secondary hover:bg-surface-elevated">
              {selectedIds.size === images.length && images.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
              {selectedIds.size === images.length && images.length > 0 ? 'Deselect all' : 'Select all'}
            </button>
            <button onClick={() => void removeSelected()} disabled={!selectedIds.size || deleting} className="flex h-9 items-center gap-2 rounded-lg bg-danger px-3 text-xs font-medium text-white disabled:opacity-40">
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete {selectedIds.size ? `(${selectedIds.size})` : ''}
            </button>
            <button onClick={exitSelect} className="h-9 rounded-lg px-3 text-xs text-text-muted hover:bg-surface-elevated hover:text-text-primary">Cancel</button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-text-muted"><Loader2 size={16} className="animate-spin" /> Loading images…</div>
      ) : images.length === 0 ? (
        <div className="flex min-h-64 items-center justify-center rounded-2xl border border-dashed border-border text-sm text-text-muted">AI-generated images will appear here.</div>
      ) : (
        <div className="grid grid-cols-3 gap-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7">
          {images.map((image, index) => (
            <GridCell
              key={image.image_id}
              image={image}
              selectMode={selectMode}
              selected={selectedIds.has(image.image_id)}
              onOpen={() => setOpenIndex(index)}
              onToggle={() => toggle(image.image_id)}
              onLongPress={() => enterSelect(image.image_id)}
            />
          ))}
        </div>
      )}

      {openIndex !== null && images[openIndex] && <ImageViewer images={images} initialIndex={openIndex} onClose={() => setOpenIndex(null)} />}
    </div>
  )
}
